import { apiClientNoPrepend, apiClientVersioned, returnData } from '../index'

import type { IPaginatedResponse } from '../index'
import type {
  IWebsiteAdminListFilters,
  IWebsiteAdminRestoreBody,
  IWebsiteAdminSummary,
  IWebsiteAdminUpdateBody,
  IWebsiteCreateBody,
  IWebsiteDetail,
  IWebsiteListFilters,
  IWebsitePermission,
  IWebsitePermissionsUpdateBody,
  IWebsitePublicHtmlOptions,
  IWebsiteSlugCheckBody,
  IWebsiteSlugCheckResponse,
  IWebsiteSummary,
  IWebsiteUpdateBody,
} from '../types/websites'

export const WebsitesApi = {
  // #region User websites
  /**
   * Create a website. Uploads inline HTML to S3 when `file` is an
   * `{ html_content }` object; links an existing file by UUID string.
   */
  create: (body: IWebsiteCreateBody) =>
    returnData(apiClientVersioned.put<IWebsiteDetail>('/websites/', body)),

  /**
   * Paginated library of the websites the user owns. Owner-scoped by default
   * (EVGPT-1596) — pass `owned_only: false` to widen it back to owned + shared.
   * `total` follows the same scope, so it is the owned count.
   */
  list: (
    filters?: Partial<IWebsiteListFilters>,
    options?: { signal?: AbortSignal },
  ) =>
    returnData(
      apiClientVersioned.get<IPaginatedResponse<IWebsiteSummary>>(
        '/websites/',
        { params: filters, signal: options?.signal },
      ),
    ),

  /** Full detail including the served file and resolved permissions. */
  get: (id: string) =>
    returnData(apiClientVersioned.get<IWebsiteDetail>(`/websites/${id}`)),

  /**
   * Partial update. `file` and `permissions` overwrite when sent; omit them to
   * leave the served page / current grantees unchanged.
   */
  update: (id: string, body: IWebsiteUpdateBody) =>
    returnData(
      apiClientVersioned.patch<IWebsiteDetail>(`/websites/${id}`, body),
    ),

  remove: (id: string) =>
    returnData(apiClientVersioned.delete<void>(`/websites/${id}`)),

  /** Replace all sharing permissions for a website. */
  updatePermissions: (id: string, body: IWebsitePermissionsUpdateBody) =>
    returnData(
      apiClientVersioned.put<IWebsitePermission[]>(
        `/websites/${id}/permissions`,
        body,
      ),
    ),

  /** Check whether a slug is available before create/update. */
  checkSlug: (body: IWebsiteSlugCheckBody) =>
    returnData(
      apiClientVersioned.post<IWebsiteSlugCheckResponse>(
        '/websites/check-slug',
        body,
      ),
    ),
  // #endregion

  // #region Admin websites
  /** Paginated list of all websites (admin only). */
  adminList: (filters?: Partial<IWebsiteAdminListFilters>) =>
    returnData(
      apiClientVersioned.get<IPaginatedResponse<IWebsiteAdminSummary>>(
        '/admin/websites',
        { params: filters },
      ),
    ),

  /** Full website detail (admin only). */
  adminGet: (id: string) =>
    returnData(apiClientVersioned.get<IWebsiteDetail>(`/admin/websites/${id}`)),

  /** Partial update of metadata and publish settings (admin only). */
  adminUpdate: (id: string, body: IWebsiteAdminUpdateBody) =>
    returnData(
      apiClientVersioned.patch<IWebsiteDetail>(`/admin/websites/${id}`, body),
    ),

  /** Delete a website (admin only). */
  adminRemove: (id: string) =>
    returnData(apiClientVersioned.delete<void>(`/admin/websites/${id}`)),

  /** Restore a soft-deleted website (admin only). */
  adminRestore: (id: string, body: IWebsiteAdminRestoreBody) =>
    returnData(
      apiClientVersioned.post<void>(`/admin/websites/${id}/restore`, body),
    ),

  /** Permanently delete a website and its S3 assets (ROOT only). */
  adminHardRemove: (id: string) =>
    returnData(
      apiClientVersioned.delete<void>(`/admin/hard-delete/websites/${id}`),
    ),
  // #endregion

  // #region Public
  /**
   * Serve a published website's HTML at `/w/{slug}`. The site holds exactly one
   * file, so the endpoint takes no file selector (EVGPT-1592).
   * Requires JWT; password-protected sites accept `X-Website-Password`.
   */
  getPublicHtml: (slug: string, options?: IWebsitePublicHtmlOptions) =>
    returnData(
      apiClientNoPrepend.get<string>(`/w/${encodeURIComponent(slug)}`, {
        responseType: 'text',
        headers: buildWebsitePublicHeaders(options),
      }),
    ),
  // NOTE: `/w/{slug}/meta` is deliberately not wrapped. It existed to list a site's
  // files so the viewer could pick one; a website now serves exactly one file
  // (EVGPT-1592), so the viewer just fetches `/w/{slug}`.
  // #endregion
}

const WEBSITE_PASSWORD_HEADER = 'X-Website-Password'

function buildWebsitePublicHeaders(
  options?: IWebsitePublicHtmlOptions,
): Record<string, string> | undefined {
  if (!options?.password) return undefined
  return { [WEBSITE_PASSWORD_HEADER]: options.password }
}
